

import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FaPlus, FaFolderOpen } from "react-icons/fa6";
import { FaCheckCircle, FaCalendarAlt, FaClock, FaChevronRight, FaArrowLeft, FaHome, FaEllipsisV, FaShareAlt, FaUserCircle, FaEye } from "react-icons/fa";
import { useHistory, useLocation } from "react-router-dom";
import FileUploader from "../comps/FileUploader.jsx";
import ShareModal from "../comps/ShareModal.jsx";

/**
 * Format a datetime string to user's local timezone with beautiful display
 * @param {string} dateString - ISO date string or any parseable date
 * @returns {Object} Formatted date parts for display
 */
function formatDateTime(dateString) {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Relative time for recent dates
    let relativeTime = '';
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        relativeTime = diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
      } else {
        relativeTime = diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
      }
    } else if (diffDays === 1) {
      relativeTime = 'Yesterday';
    } else if (diffDays < 7) {
      relativeTime = `${diffDays} days ago`;
    }

    // Format date: "Dec 26, 2025"
    const formattedDate = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Format time: "2:30 PM"
    const formattedTime = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return {
      date: formattedDate,
      time: formattedTime,
      relative: relativeTime,
      full: `${formattedDate} at ${formattedTime}`
    };
  } catch {
    return null;
  }
}

export default function CreateFolder() {
  const history = useHistory();
  const location = useLocation();
  const token = sessionStorage.getItem("userToken");
  const { space_path, username, list, space, autoOpenUploader } = location.state.state || {};

  // Auto-open uploader if navigating from "New Project" button
  const [showUploader, setShowUploader] = useState(autoOpenUploader || false);
  const [folderData, setFolderData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shareFolder, setShareFolder] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Folders across the whole organization are visible now, so offer a quick
  // way to narrow the grid to the ones the user can actually act on.
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const menuRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
  const GETFOLDER_API = `${BASE_URL}/ocr/getfiles/`;

  const fetchFolderDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(GETFOLDER_API, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      let filteredData = res.data.filter((item) => item.space_path.toLowerCase() === space_path.toLowerCase()) || [];
      setFolderData(filteredData ?? []);

    } catch (err) {
      console.error("Error fetching folder data", err);
    } finally {
      setLoading(false);
    }
  };

  const visibleFolders = showOnlyMine
    ? folderData.filter((f) => f.is_owner)
    : folderData;

  useEffect(() => {
    // Initial fetch
    fetchFolderDetails();
    const intervalId = setInterval(() => {
      fetchFolderDetails();
    }, 60000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [space_path, token]);

  // Close kebab menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Navigate to a parent space
  const navigateToSpace = (pathArray) => {
    if (pathArray.length === 0) {
      // Go back to org chart root
      history.push("/orgchart");
      return;
    }
    const selectedSpace = pathArray[pathArray.length - 1];
    sessionStorage.setItem("space", selectedSpace);
    history.push("/createfolder", {
      state: {
        list: pathArray,
        space: selectedSpace,
        username: username,
        space_path: pathArray.join("/"),
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {/* Back button and breadcrumbs */}
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => history.push("/orgchart")}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Back to Organization"
          >
            <FaArrowLeft className="w-4 h-4 text-gray-600" />
          </button>

          {/* Breadcrumb navigation */}
          <nav className="flex items-center gap-1 text-sm overflow-x-auto">
            <button
              onClick={() => history.push("/orgchart")}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-gray-600 hover:text-green-700 transition-colors whitespace-nowrap"
            >
              <FaHome className="w-3 h-3" />
              <span>Organization</span>
            </button>

            {list && list.map((segment, index) => {
              const isLast = index === list.length - 1;
              const pathToHere = list.slice(0, index + 1);

              return (
                <div key={index} className="flex items-center gap-1">
                  <FaChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  {isLast ? (
                    <span className="px-2 py-1 font-medium text-green-800 whitespace-nowrap">
                      {segment}
                    </span>
                  ) : (
                    <button
                      onClick={() => navigateToSpace(pathToHere)}
                      className="px-2 py-1 rounded hover:bg-gray-100 text-gray-600 hover:text-green-700 transition-colors whitespace-nowrap"
                    >
                      {segment}
                    </button>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Title and actions */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-green-800">
              {space || "Projects"}
            </h1>
            <p className="text-gray-600 mt-1">
              {folderData.length > 0
                ? `${folderData.length} project${folderData.length !== 1 ? 's' : ''} in this space`
                : "No projects yet - upload files to create one"
              }
            </p>
          </div>

          <button
            onClick={() => setShowUploader(true)}
            className="bg-green-800 flex items-center gap-2 rounded-lg px-4 py-2 text-white font-medium hover:bg-green-700 transition-colors"
          >
            <FaPlus />
            New Upload
          </button>
        </div>
      </div>

      {/* Ownership filter — only worth showing once someone else's folder is
          in the list, otherwise it is a no-op control. */}
      {!loading && folderData.some((f) => !f.is_owner) && (
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setShowOnlyMine(false)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              !showOnlyMine
                ? "bg-green-600 text-white"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            All projects ({folderData.length})
          </button>
          <button
            onClick={() => setShowOnlyMine(true)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              showOnlyMine
                ? "bg-green-600 text-white"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            Created by me ({folderData.filter((f) => f.is_owner).length})
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : visibleFolders.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleFolders.map((folder) => {
            const dateInfo = formatDateTime(folder.created_at);
            return (
              <div
                key={folder.folder_name}
                className="relative cursor-pointer bg-white p-6 rounded-lg shadow hover:shadow-md transition"
                onClick={() =>
                  history.push("/workspace", {
                    state: {
                      folder_name: folder.folder_name,
                      space_path: folder.space_path,
                      username: username,
                    },
                  })
                }
              >
                {/* Kebab menu — actions are limited to folders the user owns.
                    can_modify is computed by the API so the rule lives in one place. */}
                <div className="absolute top-4 right-4" ref={openMenuId === folder.id ? menuRef : null}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === folder.id ? null : folder.id);
                    }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FaEllipsisV className="w-4 h-4 text-gray-400" />
                  </button>

                  {openMenuId === folder.id && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      {folder.can_modify ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            setShareFolder(folder);
                          }}
                          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <FaShareAlt className="w-3.5 h-3.5 text-green-600" />
                          Share
                        </button>
                      ) : (
                        <div className="px-4 py-2.5 text-xs text-gray-500 leading-relaxed">
                          <div className="flex items-center gap-1.5 font-medium text-gray-600 mb-0.5">
                            <FaEye className="w-3 h-3" />
                            View only
                          </div>
                          Only {folder.owner_name || "the owner"} can change this project.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FaFolderOpen className="w-6 h-6 text-green-700" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 pr-6 truncate">{folder.folder_name}</h3>

                    {/* Owner attribution — shown on every card in the same
                        position so "mine vs. theirs" is scannable at a glance. */}
                    <div className="flex items-center gap-1.5 mt-1.5 text-xs">
                      <FaUserCircle
                        className={`w-3.5 h-3.5 shrink-0 ${
                          folder.is_owner ? "text-green-600" : "text-gray-400"
                        }`}
                      />
                      {folder.is_owner ? (
                        <span className="font-medium text-green-700">You</span>
                      ) : (
                        <span className="text-gray-500 truncate" title={folder.owner_email}>
                          {folder.owner_name || folder.owner_email}
                        </span>
                      )}
                      {!folder.can_modify && (
                        <span className="flex items-center gap-1 text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
                          <FaEye className="w-2.5 h-2.5" />
                          View only
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-1 text-green-600">
                        <FaCheckCircle />
                        processed
                      </div>
                    </div>
                  </div>
                </div>

                {/* Datetime Stamp */}
                {dateInfo && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FaCalendarAlt className="w-3.5 h-3.5 text-green-600" />
                          <span className="text-sm font-medium text-gray-700">{dateInfo.date}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FaClock className="w-3.5 h-3.5 text-green-600" />
                          <span className="text-sm text-gray-600">{dateInfo.time}</span>
                        </div>
                      </div>
                      {dateInfo.relative && (
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          {dateInfo.relative}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div
                      className="h-full bg-green-600 rounded-full"
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center p-12">
          <FaFolderOpen className="w-12 h-12 text-gray-300 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {showOnlyMine && folderData.length > 0
              ? "You haven't created any projects here"
              : "No projects yet"}
          </h3>
          {showOnlyMine && folderData.length > 0 && (
            <button
              onClick={() => setShowOnlyMine(false)}
              className="mt-2 text-sm text-green-600 hover:underline"
            >
              Show all {folderData.length} projects in this space
            </button>
          )}
        </div>
      )}


      {/* File Uploader */}
      {showUploader && (
        <FileUploader
          onClose={() => setShowUploader(false)}
          onUploadComplete={fetchFolderDetails}
        />
      )}

      {/* Share Modal */}
      {shareFolder && (
        <ShareModal
          folder={shareFolder}
          onClose={() => setShareFolder(null)}
        />
      )}
    </div>
  );
}
